import { X, Command, CornerDownLeft, ArrowUp, ArrowDown, Slash } from 'lucide-react';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string[]; description: string }[];
}

const GROUPS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['G'], description: 'Go to homepage' },
      { keys: ['D'], description: 'Go to dashboard' },
      { keys: ['A'], description: 'Go to admin' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['Ctrl', 'K'], description: 'Focus search bar' },
      { keys: ['Ctrl', '/'], description: 'Open help center' },
      { keys: ['Ctrl', 'S'], description: 'Save current view' },
    ],
  },
  {
    title: 'General',
    shortcuts: [
      { keys: ['ESC'], description: 'Close modal / go back' },
      { keys: ['?'], description: 'Show this help' },
    ],
  },
  {
    title: 'Dashboard',
    shortcuts: [
      { keys: ['1'], description: 'Heat Map tab' },
      { keys: ['2'], description: 'Saved Parcels tab' },
      { keys: ['3'], description: 'Pipeline tab' },
      { keys: ['4'], description: 'Settings tab' },
    ],
  },
];

export default function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(10,15,20,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-lg rounded-xl border overflow-hidden" style={{ background: '#232F3E', borderColor: 'rgba(247,248,244,0.08)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(186,255,41,0.15)' }}>
              <Command size={18} style={{ color: '#7CFF6B' }} />
            </div>
            <div>
              <h3 className="text-base font-semibold" style={{ color: '#F7F8F4' }}>Keyboard Shortcuts</h3>
              <p className="text-xs" style={{ color: '#A8B8AE' }}>Press these keys anywhere to navigate faster</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-shortcut="close"
            aria-label="Close keyboard shortcuts"
            className="h-8 w-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ color: '#A8B8AE' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(247,248,244,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Groups */}
        <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-2.5" style={{ color: 'rgba(245,245,243,0.4)' }}>{group.title}</h4>
              <div className="space-y-2">
                {group.shortcuts.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: 'rgba(245,245,243,0.7)' }}>{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, j) => (
                        <span key={j} className="inline-flex items-center">
                          <kbd
                            className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded text-[10px] font-mono font-medium border"
                            style={{
                              background: '#07110D',
                              borderColor: 'rgba(247,248,244,0.12)',
                              color: 'rgba(245,245,243,0.7)',
                              boxShadow: '0 1px 0 rgba(245,245,243,0.05)',
                            }}
                          >
                            {key === 'Ctrl' && <span className="text-[9px]">Ctrl</span>}
                            {key === 'ESC' && <span className="text-[9px]">Esc</span>}
                            {key === '?' && '?'}
                            {key === 'K' && 'K'}
                            {key === 'S' && 'S'}
                            {key === '/' && <Slash size={10} />}
                            {key === 'G' && 'G'}
                            {key === 'D' && 'D'}
                            {key === 'A' && 'A'}
                            {key === '1' && '1'}
                            {key === '2' && '2'}
                            {key === '3' && '3'}
                            {key === '4' && '4'}
                          </kbd>
                          {j < shortcut.keys.length - 1 && (
                            <span className="mx-1 text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>+</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t text-center" style={{ borderColor: 'rgba(247,248,244,0.08)' }}>
          <p className="text-xs" style={{ color: 'rgba(245,245,243,0.4)' }}>
            Shortcuts are disabled when typing in input fields
          </p>
        </div>
      </div>
    </div>
  );
}
